/**
 * Cache contract v3 invalidates:
 * - v1 datasets whose `wetBulbC` field was populated from dew point; and
 * - v2 datasets whose low NSRDB relative-humidity percentages could be
 *   misread as 0..1 fractions.
 */
export const TMY_DATASET_SCHEMA_VERSION = 3 as const;

export interface TmyHour {
  hour: number; // 0..8759
  month: number; // 1..12
  day: number; // 1..31
  hourOfDay: number; // 0..23
  dryBulbC: number;
  /** ASHRAE thermodynamic wet-bulb temperature derived from dry bulb, RH, and pressure. */
  wetBulbC: number;
  relativeHumidity: number; // 0..1
  pressureMPa: number;
  windSpeedMs: number;
  ghi_Wm2: number; // global horizontal irradiance
  dni_Wm2: number; // direct normal
  dhi_Wm2: number; // diffuse horizontal
}

export interface TmyDataset {
  schemaVersion: typeof TMY_DATASET_SCHEMA_VERSION;
  /** Rounded requested/site coordinate used as the stable cache-key identity. */
  latitudeRounded: number;
  /** Rounded requested/site coordinate used as the stable cache-key identity. */
  longitudeRounded: number;
  /** Nearest source-grid latitude returned by the weather provider, when supplied. */
  gridLatitude?: number;
  /** Nearest source-grid longitude returned by the weather provider, when supplied. */
  gridLongitude?: number;
  /** Provider location/grid identifier, when supplied. */
  locationId?: string;
  source: 'nrel-psm3' | 'pvgis' | 'ashrae-iwec2';
  /** Provider dataset/API version, when known. */
  sourceVersion?: string;
  /** Provider dataset selection/name, such as `tmy`, when supplied. */
  datasetName?: string;
  /** Explicit provider-declared year, or null for a multi-year composite TMY. */
  datasetYear: number | null;
  elevationM: number;
  timeZoneOffsetHours: number;
  hours: TmyHour[]; // length 8760 (sometimes 8784 for leap; trim to 8760)
  fetchedAt: string; // ISO
}

/** Auxiliary SED statistic; never a water-model/revision input or design percentile. */
export interface TmyHottestHoursSummary {
  summaryVersion: 'tmy-hottest-hours-v1';
  siteId: string;
  datasetSchemaVersion: typeof TMY_DATASET_SCHEMA_VERSION;
  datasetHash: string;
  summaryHash: string;
  source: TmyDataset['source'];
  sourceVersion?: string;
  datasetName?: string;
  datasetYear: number | null;
  latitudeRounded: number;
  longitudeRounded: number;
  gridLatitude?: number;
  gridLongitude?: number;
  locationId?: string;
  fetchedAt: string;
  datasetHourCount: 8760;
  hourCount: 35;
  meanDryBulbC: number;
  meanWetBulbC: number;
}
